// externals
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useRef, useState, useMemo } from 'react';

// components
import { AiLogoIcon } from '@makemydeal/dr-shared-components';

// libraries
import { Alert } from '@interstate/components/Alert';
import { SearchInput } from '@interstate/components/SearchInput';
import { Spinner } from '@interstate/components/Spinner';
import { Typography } from '@interstate/components/Typography';

// local components
import ChangeVehicleSearchResult from './ChangeVehicleSearchResult';
import SearchBarError, { ErrorType } from './SearchBarError';
import AiAltVehicleOptionsSection from './AiAltVehicleOptionsSection';

// store
import {
    changeVehicleSelectors,
    dismissInventoryFallback,
    fetchVehicleAgentFilterSummary,
    processVehicleAgentRequestWithFilters,
    processVehicleAgentSimilarityRequest,
    setAgentPromptAttr
} from '../../store';

// types
import type { IVehicle } from '@makemydeal/dr-platform-types';

// styles
import { SearchResultsContainer, SearchSpinnerContainer, StyledSearchBarContainer } from './SearchAndSelectVehicle.style';
import { AiSearchContainer, AiSearchContent, InputSection, InputDescription, LabelWithIcon } from './AiAssistedSearch.style';
import { useVehicleSearchFiltersContext } from './VehicleSearchFiltersContext';
import VehicleAppliedFilterTags from './VehicleAppliedFilterTags';
import { AI_PROMPT_MODE, AI_PROMPT_PLACEHOLDER, AI_REQUEST_TRIGGER, INVENTORY_FALLBACK_BANNER_TEXT } from '../../constants';

export interface AiAssistedSearchProps {
    isMobileView: boolean;
    redirectToBuildVehicleTab: (vehicle: IVehicle) => void;
}

const AiAssistedSearch = ({ isMobileView, redirectToBuildVehicleTab }: AiAssistedSearchProps) => {
    const dispatch = useDispatch();
    // Separate state for each text box
    const [userInputValue, setUserInputValue] = useState('');
    const [hasTriggeredAiSearch, setHasTriggeredAiSearch] = useState(false);
    const hasFetchedCrossDealershipFilters = useRef(false);
    // From context, not the store directly, so a host override applies here too.
    const { searchValue, appliedFilters, sortBy, updateSearchValue, existingVehicleId, currentVehicleForSimilaritySearch } =
        useVehicleSearchFiltersContext();

    const commonOrgDealerId = useSelector(changeVehicleSelectors.additionalSelectors.getCommonOrgId);
    const bffEndpoint = useSelector(changeVehicleSelectors.additionalSelectors.getServicesBff);
    const dealerships = useSelector(changeVehicleSelectors.getDealerships);
    const commonOrgDealerIds = useMemo(() => dealerships?.map((dealership) => dealership.commonOrgId) || [], [dealerships]);
    const showAllVehiclesIfNoBaseVehicle = useSelector(
        changeVehicleSelectors.additionalSelectors.showAllVehiclesIfNoBaseVehicle ?? (() => false)
    );
    const isCrossDealershipAIVehicleSearchEnabled = useSelector(
        changeVehicleSelectors.additionalSelectors.isEnableCrossDealershipAIVehicleSearch ?? (() => false)
    );

    const isPromptOptimizationEnabled = useSelector(
        changeVehicleSelectors.additionalSelectors.isPromptOptimizationEnabled ?? (() => false)
    );

    const aiPromptMode = useSelector(changeVehicleSelectors.getAgentPromptMode);
    const isAgentSearchRunning = useSelector(changeVehicleSelectors.getIsAgentSearchInProgress);
    const isAgentFallbackPending = useSelector(changeVehicleSelectors.getIsAgentFallbackPending);
    const isShowingInventoryFallback = useSelector(changeVehicleSelectors.getIsShowingInventoryFallback);
    const isAgentSearchInProgress = isAgentSearchRunning || isAgentFallbackPending;
    const agentVehicleResponse = useSelector(changeVehicleSelectors.getAgentVehicleResponse);
    const showAgentProcessingError = useSelector(changeVehicleSelectors.getShowAgentProcessingError);
    const isAiAltVehicleOptionsSectionVisible = useSelector(changeVehicleSelectors.isAiAltVehicleOptionsSectionVisible);

    const hasVehiclesAvailable = agentVehicleResponse && agentVehicleResponse.length > 0;

    const shouldShowNoResultsError = showAgentProcessingError || !hasVehiclesAvailable;

    const handleSearchClick = (event: any) => {
        const trimmedPromptString = event.target.value.trim();
        // Use 'all vehicles' prompt when search query is empty to show all inventory
        const effectivePromptString = trimmedPromptString || AI_PROMPT_PLACEHOLDER.ALL_VEHICLES;
        setUserInputValue(trimmedPromptString); // Update local state for the input box
        updateSearchValue(trimmedPromptString); // Sync context search value to trigger vs-002 CDL event via updateSearchResults

        dispatch(setAgentPromptAttr(effectivePromptString, AI_PROMPT_MODE.SEMANTIC_SEARCH)); // Update the prompt string and prompt mode in the store
        dispatch(processVehicleAgentRequestWithFilters({ appliedFilters, sortBy, trigger: AI_REQUEST_TRIGGER.USER_SEARCH })); // Dispatch action to process the agent request
    };

    const aiHandlePageChange = (selectedPage: number) => {
        dispatch(
            processVehicleAgentRequestWithFilters({
                appliedFilters,
                sortBy,
                selectedPage,
                vehicle: currentVehicleForSimilaritySearch,
                trigger: AI_REQUEST_TRIGGER.PAGINATION
            })
        );
    };

    const handleSearchClearCallback = () => {
        setUserInputValue('');
        dispatch(setAgentPromptAttr(AI_PROMPT_PLACEHOLDER.ALL_VEHICLES, AI_PROMPT_MODE.SEMANTIC_SEARCH));
        dispatch(processVehicleAgentRequestWithFilters({ appliedFilters, sortBy, trigger: AI_REQUEST_TRIGGER.SEARCH_CLEAR }));
    };

    // Auto-trigger AI search once on mount when no search value exists:
    // - With base vehicle: run similarity search
    // - Without base vehicle: fall back to all-vehicles search if host app opts in via showAllVehiclesIfNoBaseVehicle additional selector
    useEffect(() => {
        if (hasTriggeredAiSearch || searchValue) return;

        const shouldRunSimilaritySearch = existingVehicleId && currentVehicleForSimilaritySearch;
        const shouldFallbackToAllVehicles = !currentVehicleForSimilaritySearch && showAllVehiclesIfNoBaseVehicle;
        if (shouldRunSimilaritySearch || shouldFallbackToAllVehicles) {
            setUserInputValue('');

            // In single-dealer mode, commonOrgDealerId is available from INIT so fetch filter summary immediately.
            // In cross-dealership mode, dealerships load asynchronously via the bridge user flow,
            // so filter summary is deferred to a separate effect below to avoid firing with empty dealer IDs.
            if (!isCrossDealershipAIVehicleSearchEnabled) {
                dispatch(
                    fetchVehicleAgentFilterSummary(commonOrgDealerId, commonOrgDealerIds, bffEndpoint, isPromptOptimizationEnabled)
                );
            }

            if (shouldRunSimilaritySearch) {
                // DE744450: the agent middleware derives promptMode/promptString from Redux state, which
                // retains the last user search (e.g. a semantic "audi" search) across a Change Vehicle
                // close→reopen. Reset the prompt to similarity mode here so this fresh-session auto-trigger
                // actually runs a similarity search for the current vehicle instead of replaying the prior
                // search and re-rendering its cached results.
                dispatch(setAgentPromptAttr('', AI_PROMPT_MODE.SIMILARITY_SEARCH));
                dispatch(processVehicleAgentSimilarityRequest(currentVehicleForSimilaritySearch, AI_REQUEST_TRIGGER.DRAWER_OPEN));
            } else {
                dispatch(setAgentPromptAttr(AI_PROMPT_PLACEHOLDER.ALL_VEHICLES, AI_PROMPT_MODE.SEMANTIC_SEARCH));
                dispatch(
                    processVehicleAgentRequestWithFilters({ appliedFilters, sortBy, trigger: AI_REQUEST_TRIGGER.DRAWER_OPEN })
                );
            }
            setHasTriggeredAiSearch(true);
        }
    }, [
        existingVehicleId,
        hasTriggeredAiSearch,
        searchValue,
        updateSearchValue,
        commonOrgDealerId,
        commonOrgDealerIds,
        isCrossDealershipAIVehicleSearchEnabled
    ]);

    // Fetch filter summary for cross-dealership mode once dealerships become available.
    // This is separate from the mount effect because dealerships load asynchronously via the bridge user flow,
    // and we must not call fetchVehicleAgentFilterSummary with empty dealer IDs (causes error banner).
    useEffect(() => {
        if (!isCrossDealershipAIVehicleSearchEnabled || !dealerships?.length || hasFetchedCrossDealershipFilters.current) return;
        hasFetchedCrossDealershipFilters.current = true;
        dispatch(fetchVehicleAgentFilterSummary(commonOrgDealerId, commonOrgDealerIds, bffEndpoint, isPromptOptimizationEnabled));
    }, [isCrossDealershipAIVehicleSearchEnabled, dealerships, commonOrgDealerId, commonOrgDealerIds]);

    return (
        <AiSearchContainer>
            <AiSearchContent id="ai-assisted-search-content" data-testid="ai-assisted-search-content">
                <InputSection>
                    <label htmlFor="ai-search-input">
                        <LabelWithIcon>
                            <Typography variant="strong-md">Enhanced AI-Powered Search</Typography>
                            <AiLogoIcon data-testid="ai-assisted-vehicle-search-logo" fontSize="32px" ariaHidden={true} />
                        </LabelWithIcon>
                    </label>
                    <InputDescription>
                        Describe what you're looking for in plain English. Try searches like "Find red sedans with black interior"
                        or "Low mileage SUVs with rear wheel drive".
                    </InputDescription>
                    <StyledSearchBarContainer>
                        <SearchInput
                            id="ai-search-input"
                            data-testid="ai-assisted-search-vehicle-search-input"
                            name="chatTextInput"
                            placeholder="Ask me anything..."
                            inputValue={userInputValue}
                            onSearchClick={handleSearchClick}
                            onClearCallback={handleSearchClearCallback}
                            style="primary"
                        />
                    </StyledSearchBarContainer>
                </InputSection>
                <VehicleAppliedFilterTags isMobileView={isMobileView} />
            </AiSearchContent>
            {/* AVO is deal-driven, independent of the inventory search: render once here so it stays
                mounted across the spinner / results / no-results states instead of unmounting on every search. */}
            {isAiAltVehicleOptionsSectionVisible && <AiAltVehicleOptionsSection />}
            {!isAgentSearchInProgress && (
                <>
                    {/* Search Results Section - Shows structured vehicle data only for search mode */}
                    {!shouldShowNoResultsError && (
                        <SearchResultsContainer>
                            {isShowingInventoryFallback && (
                                <Alert
                                    data-testid="inventory-fallback-banner"
                                    type="info"
                                    role="status"
                                    displayCloseButton
                                    onCloseClick={() => dispatch(dismissInventoryFallback())}
                                    sx={{ margin: '16px 0 8px 0' }}
                                >
                                    <Typography variant="body-sm" tag="span">
                                        {INVENTORY_FALLBACK_BANNER_TEXT}
                                    </Typography>
                                </Alert>
                            )}
                            <ChangeVehicleSearchResult
                                searchResults={agentVehicleResponse}
                                isSearchError={false} // AI mode handles errors via shouldShowNoResultsError above
                                redirectToBuildVehicleTab={redirectToBuildVehicleTab}
                                showSortOptions={true}
                                aiHandlePageChange={aiHandlePageChange}
                            />
                        </SearchResultsContainer>
                    )}

                    {shouldShowNoResultsError && <SearchBarError errorType={ErrorType.NO_AGENT_RESULTS_FOUND} />}
                </>
            )}
            {isAgentSearchInProgress && (
                <SearchSpinnerContainer>
                    <div className="ai-search-status-container">
                        <span id="ai-search-status" data-testid="ai-search-status">
                            One moment—Processing your request
                        </span>
                        {aiPromptMode === AI_PROMPT_MODE.SIMILARITY_SEARCH && !isAgentFallbackPending && (
                            <span id="ai-search-similarity-status" data-testid="ai-search-similarity-status">
                                Searching for vehicles similar to: {existingVehicleId}
                            </span>
                        )}
                    </div>
                    <Spinner data-testid="search-vehicle-spinner" />
                </SearchSpinnerContainer>
            )}
        </AiSearchContainer>
    );
};

export default AiAssistedSearch;
